// Custom DS-Table column for the Start / End date cells. When the opportunity has the date it shows the
// formatted value; when it is missing it shows the red "…date missing" label + a "?" help trigger that opens
// a popover (MissingDatePopover) explaining the missing date, with a primary "+ Add … Date" action. A plain
// DateColumn cannot express the missing-date CTA, so we author a column (dist-internal imports are the
// accepted web-front convention).
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import type {OpportunitySortingFn, OpportunityTableRow} from '../opportunityTableRows';
import MissingDatePopover from './MissingDatePopover';
import Styled from 'styled-components';

// StyledCell sets `white-space: nowrap` and gives a cell no way to contain its content, so a
// value wider than the column spills over the neighbouring ones instead of clipping - the same
// failure the Quote Status pill had. The missing-date branch is contained inside
// MissingDatePopover instead, so that its label clips while the "?" trigger stays visible.
const DateCellContent = Styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`;

export interface DateOrAddColumnType {
	id?: string;
	accessorKey: string; // the Date field (drives sorting)
	header: string;
	visible?: boolean;
	align?: Align;
	'data-cy'?: string;
	allowSorting?: boolean;
	sortingFn?: OpportunitySortingFn;
	width?: string;
	// The preformatted date label for this row, or null when the date is missing.
	dateLabel: (row: OpportunityTableRow) => string | null;
	// Wraps the present-date value (the call site supplies the EditableCell).
	cellContent: (content: React.ReactNode, row: OpportunityTableRow) => React.ReactNode;
	// The date editor, rendered as step two of the missing-date popover.
	editor: (args: {close: () => void}, row: OpportunityTableRow) => React.ReactNode;
	// Whether this row may be edited. The missing-date CTA opens the same editor as the cell itself, so
	// it needs the same gate - without it a viewer who cannot edit still gets an editor here, and the
	// save is rejected by the backend rather than never offered.
	canEdit: (row: OpportunityTableRow) => boolean;
	missingLabel: string; // the red inline label, e.g. "Start date missing"
	missingTitle: string; // popover title, e.g. "Start Date Missing"
	missingBody: string; // popover body copy
	missingActionLabel: string; // popover primary-button copy ("Add Date")
	closeLabel: string; // accessible label for the popover close button
	missingDataCy: string; // data-cy for the "?" trigger (add-start-date / add-end-date)
}

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DateOrAddColumn = (_: DateOrAddColumnType): null => null;

DateOrAddColumn.toColumnDef = (child: React.ReactElement<DateOrAddColumnType>): ColumnDef<OpportunityTableRow> => {
	const {
		accessorKey,
		sortingFn,
		dateLabel,
		cellContent,
		editor,
		canEdit,
		missingLabel,
		missingTitle,
		missingBody,
		missingActionLabel,
		closeLabel,
		missingDataCy,
		align,
	} = child.props;
	return {
		...defaultColumnDef(child.props),
		accessorKey,
		sortingFn,
		cell: ({row}: {row: {original: OpportunityTableRow}}) => {
			const label = dateLabel(row.original);
			if (label) {
				return (
					<StyledCell align={align} data-cy="opportunity-date-cell">
						{cellContent(<DateCellContent>{label}</DateCellContent>, row.original)}
					</StyledCell>
				);
			}
			// D15: the start-date half behaves identically - the missing branch stays symmetric.
			return (
				<StyledCell align={align} data-cy="opportunity-date-cell">
					<MissingDatePopover
						label={missingLabel}
						title={missingTitle}
						body={missingBody}
						actionLabel={missingActionLabel}
						closeLabel={closeLabel}
						triggerDataCy={missingDataCy}
						editor={args => editor(args, row.original)}
						canEdit={canEdit(row.original)}
					/>
				</StyledCell>
			);
		},
	};
};

export default DateOrAddColumn;
